import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
export const ReviewStatus = {
  PUBLISHED: "PUBLISHED",
  FLAGGED: "FLAGGED",
  REMOVED: "REMOVED",
} as const;
export type ReviewStatus = (typeof ReviewStatus)[keyof typeof ReviewStatus];
import { del, get, put } from "../services/apiClient";

export interface ReviewRow {
  id: string;
  bookingId: string;
  customerId: string;
  centreId: string;
  ratingOverall: number;
  ratingCleanliness?: number | null;
  ratingFood?: number | null;
  ratingStaff?: number | null;
  ratingFacilities?: number | null;
  reviewText?: string | null;
  photos: string[];
  status: ReviewStatus | string;
  flaggedReason?: string | null;
  providerReply?: string | null;
  providerRepliedAt?: string | null;
  createdAt: string;
  customer?: {
    user?: { phone: string; email: string | null };
    name?: string;
  } | null;
  centre?: { id: string; name: string } | null;
}

export interface ReviewListParams {
  page?: number;
  limit?: number;
  status?: ReviewStatus;
  businessId?: string;
  centreId?: string;
  customerId?: string;
}

interface ReviewListResponse {
  data: ReviewRow[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export const reviewKeys = {
  all: ["reviews"] as const,
  list: (p: ReviewListParams) => [...reviewKeys.all, "list", p] as const,
};

export const useReviewList = (
  params: ReviewListParams = {},
  options?: Omit<UseQueryOptions<ReviewListResponse>, "queryKey" | "queryFn">,
) =>
  useQuery<ReviewListResponse>({
    queryKey: reviewKeys.list(params),
    queryFn: () => get<ReviewListResponse>("/reviews", params),
    ...options,
  });

export const useModerateReview = () => {
  const qc = useQueryClient();
  return useMutation<
    ReviewRow,
    Error,
    { id: string; status: ReviewStatus; flaggedReason?: string }
  >({
    mutationFn: ({ id, status, flaggedReason }) =>
      put<ReviewRow>(`/reviews/${id}/moderate`, { status, flaggedReason }),
    onSuccess: () => qc.invalidateQueries({ queryKey: reviewKeys.all }),
  });
};

export const useDeleteReview = () => {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => del<void>(`/reviews/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: reviewKeys.all }),
  });
};

export const useReplyReview = () => {
  const qc = useQueryClient();
  return useMutation<ReviewRow, Error, { id: string; replyText: string }>({
    mutationFn: ({ id, replyText }) =>
      put<ReviewRow>(`/reviews/${id}/reply`, { replyText }),
    onSuccess: () => qc.invalidateQueries({ queryKey: reviewKeys.all }),
  });
};
